import confetti from 'canvas-confetti';

/** Milestone definitions — keyed by metric type. */
export const MILESTONES = {
  customers: [
    { n: 1,   label: 'First Paying Customer',   headline: 'First close. The journey starts here.', emoji: '🎯' },
    { n: 5,   label: '5 Customers',              headline: 'Five gyms running on HuscleOS.',        emoji: '✋' },
    { n: 10,  label: '10 Customers',             headline: 'Double digits. Serious traction.',     emoji: '🔥' },
    { n: 25,  label: '25 Customers',             headline: 'You are building a real company.',     emoji: '🚀' },
    { n: 50,  label: '50 Customers',             headline: 'Half a hundred. Momentum compounding.',emoji: '⚡' },
    { n: 100, label: '100 Customers',            headline: 'Triple-digit club. Legacy-building.',  emoji: '💯' },
    { n: 250, label: '250 Customers',            headline: 'Category leader territory.',           emoji: '👑' },
    { n: 500, label: '500 Customers',            headline: 'The HuscleOS army.',                   emoji: '🏆' },
  ],
  mrr: [
    { n: 1000,   label: '$1K MRR',   headline: 'First grand. Real money.',                emoji: '💵' },
    { n: 5000,   label: '$5K MRR',   headline: 'Ramen profitable.',                        emoji: '🍜' },
    { n: 10000,  label: '$10K MRR',  headline: 'Five figures a month. Let it compound.',   emoji: '💰' },
    { n: 25000,  label: '$25K MRR',  headline: 'You have a real SaaS business.',           emoji: '💎' },
    { n: 50000,  label: '$50K MRR',  headline: 'Mid-six-figures ARR. The flywheel works.', emoji: '🏅' },
    { n: 100000, label: '$100K MRR', headline: 'Seven-figure ARR. You made it.',           emoji: '🎆' },
  ],
};

const STORAGE_KEY = 'huscleos_milestones_celebrated_v1';

function getCelebrated() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function markCelebrated(key) {
  const current = getCelebrated();
  if (!current.includes(key)) {
    current.push(key);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  }
}

/** Fire the confetti animation. Tasteful — 2 bursts, red + gold, short duration. */
function fireConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ['#FF2E00', '#FFB800', '#FFFFFF', '#0F0F0F'];

  // Left side burst
  confetti({
    particleCount: 60,
    spread: 70,
    origin: { x: 0.1, y: 0.4 },
    colors,
    scalar: 1.1,
  });
  // Right side burst
  confetti({
    particleCount: 60,
    spread: 70,
    origin: { x: 0.9, y: 0.4 },
    colors,
    scalar: 1.1,
  });

  // Continuous light sprinkle
  (function frame() {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.5 },
      colors,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.5 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

/**
 * Check the supplied metrics and return any newly-crossed milestone (and celebrate it).
 * Returns { key, label, headline, emoji } for the biggest new milestone crossed, or null.
 */
export function checkMilestones({ customers = 0, mrr = 0 }) {
  const celebrated = getCelebrated();
  let biggest = null;

  for (const m of MILESTONES.customers) {
    const key = `customers_${m.n}`;
    if (customers >= m.n && !celebrated.includes(key)) {
      markCelebrated(key);
      if (!biggest || m.n > (biggest.n || 0)) biggest = { ...m, key, type: 'customers' };
    }
  }
  for (const m of MILESTONES.mrr) {
    const key = `mrr_${m.n}`;
    if (mrr >= m.n && !celebrated.includes(key)) {
      markCelebrated(key);
      // MRR milestones trump customer milestones when both cross at once — bigger revenue = bigger celebration
      if (!biggest || m.n > (biggest.n || 0)) biggest = { ...m, key, type: 'mrr' };
    }
  }

  if (biggest) fireConfetti();
  return biggest;
}

/** Manual trigger — used for preview/testing. */
export function celebrateNow() { fireConfetti(); }

/** Reset all celebrated milestones (dev helper — wire a button in dev UI if needed). */
export function resetMilestones() {
  localStorage.removeItem(STORAGE_KEY);
}
